import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

const PROFILE_QUERY_MS = 12_000;

function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label}_timeout`)), ms)
    ),
  ]);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);

  const loadProfile = useCallback(async (authUser) => {
    if (!authUser) {
      setUser(null);
      setWorkspace(null);
      return;
    }

    // Fallback shape so the app can always render once the auth session is
    // established, even if the `users` row isn't readable yet (slow trigger,
    // RLS edge case, missing backfill, etc.). This prevents the user from
    // being permanently stuck on /login after a successful sign-in.
    const fallbackUser = {
      user_id: null,
      auth_id: authUser.id,
      email: authUser.email,
      name:
        authUser.user_metadata?.full_name ||
        authUser.user_metadata?.name ||
        (authUser.email ? authUser.email.split('@')[0] : 'You'),
      picture: authUser.user_metadata?.avatar_url || '',
    };

    try {
      let profileRow;
      try {
        const res = await withTimeout(
          supabase.from('users').select('*').eq('auth_id', authUser.id).maybeSingle(),
          PROFILE_QUERY_MS,
          'users_lookup'
        );
        profileRow = res.data;
        if (res.error) console.warn('[auth] profile lookup failed', res.error);
      } catch (e) {
        console.warn('[auth] profile lookup stalled or timed out', e?.message || e);
        profileRow = null;
      }

      if (profileRow) {
        setUser(profileRow);
        let memberships = [];
        try {
          const mRes = await withTimeout(
            supabase
              .from('workspace_members')
              .select('workspace_id')
              .eq('user_id', profileRow.user_id),
            PROFILE_QUERY_MS,
            'workspace_members'
          );
          memberships = mRes.data || [];
          if (mRes.error) console.warn('[auth] memberships lookup failed', mRes.error);
        } catch (e) {
          console.warn('[auth] memberships stalled or timed out', e?.message || e);
        }

        if (memberships?.length > 0) {
          const sorted = [...memberships].sort((a, b) =>
            String(a.workspace_id).localeCompare(String(b.workspace_id))
          );
          try {
            const wsRes = await withTimeout(
              supabase.from('workspaces').select('*').eq('workspace_id', sorted[0].workspace_id).maybeSingle(),
              PROFILE_QUERY_MS,
              'workspace'
            );
            if (wsRes.error) console.warn('[auth] workspace lookup failed', wsRes.error);
            setWorkspace(wsRes.data || null);
          } catch (e) {
            console.warn('[auth] workspace stalled or timed out', e?.message || e);
            setWorkspace(null);
          }
        } else {
          setWorkspace(null);
        }
      } else {
        setUser(fallbackUser);
        setWorkspace(null);
      }
    } catch (err) {
      console.error('[auth] profile load error', err);
      setUser(fallbackUser);
      setWorkspace(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      let session;
      try {
        const sessionRes = await withTimeout(supabase.auth.getSession(), PROFILE_QUERY_MS, 'getSession');
        session = sessionRes.data.session;
      } catch (e) {
        console.warn('[auth] getSession stalled or timed out', e?.message || e);
        session = null;
      }
      if (session?.user) {
        await loadProfile(session.user);
      } else {
        setUser(null);
        setWorkspace(null);
      }
    } catch {
      setUser(null);
      setWorkspace(null);
    } finally {
      setLoading(false);
    }
  }, [loadProfile]);

  useEffect(() => {
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'INITIAL_SESSION') {
        if (session?.user) {
          await loadProfile(session.user);
        } else {
          setUser(null);
          setWorkspace(null);
        }
        setLoading(false);
        return;
      }
      if (event === 'SIGNED_IN' && session?.user) {
        await loadProfile(session.user);
        setLoading(false);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setWorkspace(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkAuth, loadProfile]);

  const loginWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const loginWithGithub = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const authRedirectUrl = () => `${window.location.origin}/auth/callback`;

  const signInWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Eagerly hydrate the profile so callers can navigate immediately
    // without ProtectedRoute bouncing back to /login due to user=null.
    // The LoginPage keeps its own `submitting` flag for button UX, so we
    // don't toggle the global `loading` state here.
    if (data?.user) {
      await loadProfile(data.user);
    }
  };

  /** Returns { needsEmailConfirmation: boolean } on success */
  const signUpWithEmail = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: authRedirectUrl(),
        data: fullName ? { full_name: fullName, name: fullName } : undefined,
      },
    });
    if (error) throw error;
    const needsEmailConfirmation = Boolean(data.user && !data.session);
    if (!needsEmailConfirmation && data?.user) {
      await loadProfile(data.user);
    }
    return { needsEmailConfirmation };
  };

  const resetPasswordForEmail = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setWorkspace(null);
  };

  return (
    <AuthContext.Provider value={{
      user, setUser, loading, logout, checkAuth,
      workspace, setWorkspace,
      loginWithGoogle, loginWithGithub,
      signInWithEmail, signUpWithEmail, resetPasswordForEmail, updatePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
